import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaSearch, FaCar, FaWrench, FaBook, FaHistory, FaChartLine, FaHandshake, FaNewspaper } from 'react-icons/fa';
import BlogCard from '../components/BlogCard';

const BlogPage = () => {
    // State management
    const [blogs, setBlogs] = useState([]);
    const [featuredBlogs, setFeaturedBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({});

    // Kategori konfigürasyonu (OtoSOR benzeri)
    const categories = {
        all: { title: 'Tümü', icon: FaNewspaper, color: '#6c757d' },
        'otomobil-dunyasi': { title: 'Otomobil Dünyası', icon: FaCar, color: '#000000' },
        'arac-bakimi': { title: 'Araç Bakımı', icon: FaWrench, color: '#28a745' },
        'otomobil-sozlugu': { title: 'Otomobil Sözlüğü', icon: FaBook, color: '#ffc107' },
        'finansman-ipuclari': { title: 'Finansman İpuçları', icon: FaChartLine, color: '#20c997' },
        'satış-tavsiyeleri': { title: 'Satış Tavsiyeleri', icon: FaHandshake, color: '#fd7e14' },
    };

    // API fonksiyonları
    const fetchBlogs = async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                page: currentPage,
                limit: 12,
                sort: sortBy,
                ...(activeCategory !== 'all' && { category: activeCategory }),
                ...(searchTerm && { search: searchTerm }),
                ...params
            });

            const response = await fetch(`/api/blogs?${queryParams}`);
            const data = await response.json();

            if (data.success) {
                setBlogs(data.data.blogs);
                setPagination(data.data.pagination);
            } else {
                setError(data.message || 'Bloglar yüklenirken hata oluştu');
            }
        } catch (err) {
            console.error('Blog fetch error:', err);
            setError('Bağlantı hatası oluştu');
        }
    };

    const fetchFeaturedBlogs = async () => {
        try {
            const response = await fetch('/api/blogs/featured?limit=2');
            const data = await response.json();

            if (data.success) {
                setFeaturedBlogs(data.data);
            }
        } catch (err) {
            console.error('Featured blogs fetch error:', err);
        }
    };

    // Effects
    useEffect(() => {
        const loadBlogs = async () => {
            setLoading(true);
            await Promise.all([
                fetchBlogs(),
                fetchFeaturedBlogs()
            ]);
            setLoading(false);
        };

        loadBlogs();
    }, [activeCategory, sortBy, currentPage]);

    // Arama işlevi
    const handleSearch = async (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setLoading(true);
        await fetchBlogs({ page: 1 });
        setLoading(false);
    };

    // Kategori değiştirme
    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setCurrentPage(1);
        setSearchTerm('');
    };

    // Sayfa değiştirme
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Pagination component
    const Pagination = () => {
        if (!pagination.pages || pagination.pages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <div className="d-flex justify-content-center align-items-center mt-4">
                <Button
                    variant="outline-dark"
                    size="sm"
                    disabled={!pagination.hasPrev}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="me-2"
                >
                    « Önceki
                </Button>

                {pages.map(page => (
                    <Button
                        key={page}
                        variant={page === currentPage ? "dark" : "outline-dark"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="mx-1"
                    >
                        {page}
                    </Button>
                ))}

                <Button
                    variant="outline-dark"
                    size="sm"
                    disabled={!pagination.hasNext}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="ms-2"
                >
                    Sonraki »
                </Button>
            </div>
        );
    };

    return (
        <div className="blog-page">
            {/* HERO SECTION - AboutUs Style */}
            <section className="py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h1 className="section-title">OTOPRİME BLOG</h1>
                        <p className="lead">
                            Otomobil dünyasından en güncel haberler, araç bakım ipuçları ve finansman tavsiyeleri ile araç alım satımında uzman rehberliği
                        </p>

                        {/* Simple Search */}
                        <Row className="justify-content-center">
                            <Col lg={6} md={8}>
                                <form onSubmit={handleSearch}>
                                    <div className="input-group input-group-lg">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Blog yazılarında ara..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button
                                            type="submit"
                                            className="btn btn-dark"
                                            disabled={loading}
                                        >
                                            <FaSearch />
                                        </button>
                                    </div>
                                </form>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </section>

            {/* FEATURED BLOGS SECTION - OtoSOR Style */}
            {featuredBlogs.length > 0 && (
                <section className="py-5">
                    <Container> 
                        <Row>
                            {featuredBlogs.map((blog) => (
                                <Col lg={6} className="mb-4" key={blog._id}>
                                    <BlogCard
                                        blog={blog}
                                        variant="horizontal"
                                        featured={true}
                                        showExcerpt={false}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>
            )}

            {/* MAIN BLOG SECTION - Clean Layout */}
            <section className="py-5 bg-light">
                <Container>
                    {/* Category Navigation - Clean */}
                    <div className="text-center mb-5">
                        <Row className="justify-content-center">
                            {Object.entries(categories).map(([key, config]) => {
                                const IconComponent = config.icon;
                                return (
                                    <Col xs="auto" key={key} className="mb-2">
                                        <button
                                            className={`btn ${activeCategory === key ? 'btn-dark' : 'btn-outline-dark'} btn-sm`}
                                            onClick={() => handleCategoryChange(key)}
                                        >
                                            <IconComponent className="me-1" />
                                            {config.title}
                                        </button>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>

                    {/* Loading State */}
                    {loading && (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="mt-2">Bloglar yükleniyor...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <Alert variant="danger" className="text-center">
                            {error}
                        </Alert>
                    )}

                    {/* Blog Grid with BlogCard Component */}
                    {!loading && !error && (
                        <>
                            {blogs.length > 0 ? (
                                <>
                                    <Row>
                                        {blogs.map((blog) => (
                                            <Col lg={4} md={6} className="mb-4" key={blog._id}>
                                                <BlogCard
                                                    blog={blog}
                                                    variant="default"
                                                    showExcerpt={true}
                                                />
                                            </Col>
                                        ))}
                                    </Row>

                                    {/* Pagination */}
                                    <Pagination />

                                    {/* Sonuç Bilgisi */}
                                    <div className="text-center mt-4">
                                        <small className="text-muted">
                                            {pagination.total} yazıdan {blogs.length} tanesi gösteriliyor
                                            (Sayfa {currentPage} / {pagination.pages})
                                        </small>
                                    </div>
                                </>
                            ) : (
                                <div className="no-blogs-found text-center py-5">
                                    <FaSearch size={64} className="text-muted mb-3" />
                                    <h4>Blog yazısı bulunamadı</h4>
                                    <p className="text-muted">
                                        {searchTerm ?
                                            `"${searchTerm}" için sonuç bulunamadı. Başka bir arama terimi deneyin.` :
                                            'Bu kategoride henüz blog yazısı bulunmuyor.'
                                        }
                                    </p>
                                    {searchTerm && (
                                        <Button
                                            variant="dark"
                                            onClick={() => {
                                                setSearchTerm('');
                                                setActiveCategory('all');
                                            }}
                                        >
                                            Tüm Blogları Göster
                                        </Button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </Container>
            </section>

            {/* CTA SECTION - AboutUs Style */}
            <section className="py-5">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={8}>
                            <h3>Merak Ettiğiniz Bir Konu Var mı?</h3>
                            <p className="lead mb-4">
                                Blog'da yer almasını istediğiniz konular için bizimle iletişime geçin
                            </p>
                            <Link
                                to="/contact"
                                className="btn btn-dark btn-lg me-3"
                            >
                                İletişime Geç
                            </Link>
                            <Link
                                to="/faq"
                                className="btn btn-outline-dark btn-lg"
                            >
                                Sık Sorulan Sorular
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default BlogPage;